/**
 * Permission Utility Functions
 * 
 * Helper functions for checking user permissions in a pure, reusable way.
 * These functions can be used in Redux selectors, React components, or anywhere
 * permission checks are needed.
 */

import { Permission, Resource, Action, createPermission } from '../types/authTypes';

// ============================================================================
// Core Permission Checking Functions
// ============================================================================

/**
 * Check if user has a specific permission
 * 
 * @param userPermissions - Array of user's permissions
 * @param required - Required permission to check
 * @returns True if user has the permission
 * 
 * @example
 * const canEdit = hasPermission(user.permissions, 'section2:edit');
 */
export function hasPermission(userPermissions: Permission[], required: Permission): boolean {
    return userPermissions.includes(required);
}

/**
 * Check if user has ANY of the required permissions
 * 
 * @param userPermissions - Array of user's permissions
 * @param required - Array of permissions (user needs at least one)
 * @returns True if user has at least one of the required permissions
 * 
 * @example
 * const canAccess = hasAnyPermission(
 *   user.permissions, 
 *   ['section2:read', 'section2:edit']
 * );
 */
export function hasAnyPermission(
    userPermissions: Permission[],
    required: Permission[]
): boolean {
    return required.some((permission) => userPermissions.includes(permission));
}

/**
 * Check if user has ALL of the required permissions
 * 
 * @param userPermissions - Array of user's permissions
 * @param required - Array of permissions (user needs all)
 * @returns True if user has all required permissions
 * 
 * @example
 * const canFullyManage = hasAllPermissions(
 *   user.permissions,
 *   ['section2:read', 'section2:edit', 'section2:delete']
 * );
 */
export function hasAllPermissions(
    userPermissions: Permission[],
    required: Permission[]
): boolean {
    return required.every((permission) => userPermissions.includes(permission));
}

// ============================================================================
// Resource-Specific Shortcut Functions
// ============================================================================

/**
 * Check if user can read a specific resource
 * 
 * @param userPermissions - Array of user's permissions
 * @param resource - Resource to check (can be Resource enum or string)
 * @returns True if user can read the resource
 * 
 * @example
 * const canReadSection2 = canRead(user.permissions, Resource.SECTION2);
 * const canReadDashboard = canRead(user.permissions, 'dashboard');
 */
export function canRead(userPermissions: Permission[], resource: Resource): boolean {
    const permission = createPermission(resource, Action.READ);
    return hasPermission(userPermissions, permission);
}

/**
 * Check if user may perform an arbitrary action on a resource
 *
 * The generic form behind `canRead`/`canCreate`/`canEdit`/`canDelete`; use it where the action
 * is a variable (e.g. a table of controls each gated on a different action).
 */
export function can(userPermissions: Permission[], resource: Resource, action: Action): boolean {
    return hasPermission(userPermissions, createPermission(resource, action));
}

/**
 * Check if user can create items in a specific resource
 * 
 * @param userPermissions - Array of user's permissions
 * @param resource - Resource to check
 * @returns True if user can create in the resource
 */
export function canCreate(userPermissions: Permission[], resource: Resource): boolean {
    const permission = createPermission(resource, Action.CREATE);
    return hasPermission(userPermissions, permission);
}

/**
 * Check if user can edit a specific resource
 * 
 * @param userPermissions - Array of user's permissions
 * @param resource - Resource to check
 * @returns True if user can edit the resource
 */
export function canEdit(userPermissions: Permission[], resource: Resource): boolean {
    const permission = createPermission(resource, Action.UPDATE);
    return hasPermission(userPermissions, permission);
}

/**
 * Check if user can delete items from a specific resource
 * 
 * @param userPermissions - Array of user's permissions
 * @param resource - Resource to check
 * @returns True if user can delete from the resource
 */
export function canDelete(userPermissions: Permission[], resource: Resource): boolean {
    const permission = createPermission(resource, Action.DELETE);
    return hasPermission(userPermissions, permission);
}

// ============================================================================
// Advanced Permission Checks
// ============================================================================

/**
 * Check if user has full access to a resource (all CRUD operations)
 * 
 * @param userPermissions - Array of user's permissions
 * @param resource - Resource to check
 * @returns True if user has read, create, edit, and delete permissions
 */
export function hasFullAccess(userPermissions: Permission[], resource: Resource): boolean {
    return hasAllPermissions(userPermissions, [
        createPermission(resource, Action.READ),
        createPermission(resource, Action.CREATE),
        createPermission(resource, Action.UPDATE),
        createPermission(resource, Action.DELETE),
    ]);
}

/**
 * Check if user has at least read-only access to a resource
 * 
 * @param userPermissions - Array of user's permissions
 * @param resource - Resource to check
 * @returns True if user has read permission
 */
export function hasReadOnlyAccess(
    userPermissions: Permission[],
    resource: Resource
): boolean {
    return canRead(userPermissions, resource);
}

/**
 * Get the level of access a user has to a resource
 * 
 * @param userPermissions - Array of user's permissions
 * @param resource - Resource to check
 * @returns Object with boolean flags for each action type
 */
export function getResourceAccess(userPermissions: Permission[], resource: Resource) {
    return {
        canRead: canRead(userPermissions, resource),
        canCreate: canCreate(userPermissions, resource),
        canEdit: canEdit(userPermissions, resource),
        canDelete: canDelete(userPermissions, resource),
    };
}
